import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { CheckCircle2, Download, FileSpreadsheet, FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/FilterBar";
import { KpiCard, Panel } from "@/components/KpiCard";
import { DeclareDialog } from "@/components/DeclareDialog";
import { OccupationPanel } from "@/components/OccupationPanel";
import { ThTropPanel } from "@/components/ThTropPanel";
import { FraisBookingPanel } from "@/components/FraisBookingPanel";
import { DepensesPanel } from "@/components/DepensesPanel";
import { PenalitesPanel } from "@/components/PenalitesPanel";
import { ThPercuePanel } from "@/components/ThPercuePanel";
import { HypothequePanel } from "@/components/HypothequePanel";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, download, errMsg, money, telechargerPost } from "@/lib/api";
import { donneesExport } from "@/lib/tableTri";

const RESUME = [["Réservations", "nb_reservations", "int"], ["Nuits", "nuits", "int"], ["Revenus bruts", "revenus_bruts", "money"],
  ["Commissions", "commission", "money"], ["Charges affectées", "charges", "money"], ["Bénéfice net", "benefice_net", "money"],
  ["TPS attendue", "tps_attendue", "money"], ["TVQ attendue", "tvq_attendue", "money"], ["Taxes remises par les plateformes", "taxes_versees", "money"],
  ["Écart à régulariser", "ecart", "money"]];

const tableauxAffiches = () => [...document.querySelectorAll('[data-testid="bilan-page"] table')].map((t, i) => ({
  titre: t.dataset.exportTitre || t.closest("section")?.querySelector("h2")?.textContent.trim() || `Tableau ${i + 1}`,
  ...donneesExport(t, { sansActions: true }),
}));

const Th = ({ children, left, noExport }) => <th data-st-export={noExport ? "non" : undefined} className={`py-2 px-3 text-[11px] font-mono uppercase tracking-wider text-slate-500 whitespace-nowrap ${left ? "text-left" : "text-right"}`}>{children}</th>;
const Td = ({ children, className = "", testId }) => <td className={`py-2.5 px-3 text-right tabular ${className}`} data-testid={testId}>{children}</td>;

const LogementsTable = ({ rows, totaux, onFiche, fiche }) => (
  <div className="overflow-x-auto -mx-5">
    <SmartTable id="bilan-logements" data-export-titre="Par logement" className="w-full text-sm min-w-[1200px]" data-testid="bilan-logements-table">
      <thead><tr><Th left>Logement</Th><Th>Rés.</Th><Th>Nuits</Th><Th>Revenus bruts</Th><Th>Airbnb</Th><Th>Booking</Th><Th>Loyers / directs</Th><Th>Commissions</Th><Th>Charges</Th><Th>Bénéfice net</Th><Th>TPS att.</Th><Th>TVQ att.</Th><Th>Taxes remises</Th><Th>Écart</Th><Th noExport>Fiche</Th></tr></thead>
      <tbody>
        {[...rows, { ...totaux, logement: "TOTAL", airbnb: rows.reduce((s, r) => s + r.airbnb, 0), booking: rows.reduce((s, r) => s + r.booking, 0) }].map((r) => (
          <tr key={r.logement} className={`border-t border-slate-100 ${r.logement === "TOTAL" ? "bg-slate-50 font-semibold" : "hover:bg-slate-50"}`} data-st-fixe={r.logement === "TOTAL" ? "" : undefined} data-testid={`bilan-logement-row-${r.logement}`}>
            <td className="py-2.5 px-3 font-medium">{r.logement}{r.adresse && <span className="block text-xs font-normal text-slate-500" data-testid={`bilan-adresse-${r.logement}`}>{r.adresse}</span>}</td>
            <Td>{r.nb_reservations}</Td><Td>{r.nuits}</Td><Td testId={`bilan-revenus-${r.logement}`}>{money(r.revenus_bruts)}</Td>
            <Td>{money(r.airbnb)}</Td><Td>{money(r.booking)}</Td><Td testId={`bilan-directs-${r.logement}`}>{money(r.revenus_directs || 0)}</Td><Td>{money(r.commission)}</Td>
            <Td testId={`bilan-charges-${r.logement}`}>{money(r.charges)}</Td>
            <Td className={r.benefice_net < 0 ? "text-red-600 font-semibold" : "text-emerald-700 font-semibold"} testId={`bilan-benefice-${r.logement}`}>{money(r.benefice_net)}</Td>
            <Td>{money(r.tps_attendue)}</Td><Td>{money(r.tvq_attendue)}</Td><Td>{money(r.taxes_versees)}</Td>
            <Td className={r.ecart > 0.05 ? "text-red-600" : ""} testId={`bilan-ecart-${r.logement}`}>{money(r.ecart)}</Td>
            <Td>{r.logement !== "TOTAL" && (
              <Button size="sm" variant="outline" className="h-8" disabled={fiche === r.logement} onClick={() => onFiche(r.logement)} data-testid={`bilan-fiche-${r.logement}`}>
                {fiche === r.logement ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}<span className="ml-1">PDF</span>
              </Button>)}</Td>
          </tr>
        ))}
      </tbody>
    </SmartTable>
  </div>
);

const TrimestresTable = ({ annee, rows, onDeclare, onUndo }) => (
  <div className="overflow-x-auto -mx-5">
    <SmartTable id="bilan-trimestres" data-export-titre="Par trimestre" className="w-full text-sm min-w-[1060px]" data-testid="bilan-trimestres-table">
      <thead><tr><Th left>Trimestre</Th><Th>Revenus</Th><Th>Commissions</Th><Th>TPS</Th><Th>TVQ</Th><Th>Écart</Th><Th>Taxe séjour</Th><Th>dont perçue</Th><Th>Échéance</Th><Th left>Déclaration</Th></tr></thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.trimestre} className="border-t border-slate-100" data-testid={`bilan-trimestre-row-${t.trimestre}`}>
            <td className="py-2.5 px-3 font-medium" data-st-export={`T${t.trimestre} ${annee}`}>T{t.trimestre} {annee}</td>
            <Td>{money(t.revenus_bruts)}</Td><Td>{money(t.commission)}</Td><Td>{money(t.tps_attendue)}</Td><Td>{money(t.tvq_attendue)}</Td>
            <Td className={t.ecart > 0.05 ? "text-red-600" : ""}>{money(t.ecart)}</Td>
            <Td className="text-amber-700" testId={`bilan-th-trimestre-${t.trimestre}`}>{money(t.th_a_remettre || 0)}</Td><Td>{money(t.th_percue || 0)}</Td>
            <Td className="text-xs">{t.echeance}</Td>
            <td className="py-2 px-3" data-st-export={t.declaration ? `Déclaré le ${t.declaration.date_declaration} · ${money(t.declaration.total)}` : "Non déclaré"}>
              {t.declaration ? (
                <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid={`bilan-declared-${t.trimestre}`}>
                  <CheckCircle2 className="h-4 w-4" />Déclaré le {t.declaration.date_declaration} · {money(t.declaration.total)}
                  <button onClick={() => onUndo(t.declaration)} className="text-slate-400 hover:text-red-600" data-testid={`bilan-undeclare-${t.trimestre}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="h-8" onClick={() => onDeclare({ annee, trimestre: t.trimestre, tps: t.tps_attendue, tvq: t.tvq_attendue })} data-testid={`bilan-declare-${t.trimestre}`}>Marquer déclaré</Button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </SmartTable>
  </div>
);

export default function Bilan() {
  const { filters, options } = useFilters();
  const years = [...new Set([String(new Date().getFullYear()), ...options.mois.map((m) => m.slice(0, 4))])].sort().reverse();
  const [annee, setAnnee] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [declare, setDeclare] = useState(null);
  const [dl, setDl] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [fiche, setFiche] = useState("");
  const params = cleanParams({ annee, plateforme: filters.plateforme });
  const exportFiche = async (logement) => {
    setFiche(logement);
    try { await download("/bilan/logement-pdf", { ...params, logement }, `Fiche ${logement} ${annee}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setFiche(""); }
  };

  const load = useCallback(() => {
    api.get("/bilan", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee, filters.plateforme]);
  useEffect(load, [load]);

  const undo = async (d) => {
    if (!window.confirm(`Annuler la déclaration T${d.trimestre} ${d.annee} ?`)) return;
    try { await api.delete(`/declarations/${d.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [tous, setTous] = useState(false);
  const exportTableaux = async () => {
    setTous(true);
    const resume = RESUME.map(([l, k, f]) => [l, { v: data.totaux[k] ?? null, f }]);
    try { await telechargerPost("/bilan/export-tableaux", { feuilles: tableauxAffiches(), resume }, params, `bilan_${annee}_tableaux.xlsx`); } catch (e) { toast.error(errMsg(e)); } finally { setTous(false); }
  };
  const exportPdf = async () => {
    setPdf(true);
    try { await download("/bilan/pdf", params, `bilan_${annee}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setPdf(false); }
  };
  const exportXlsx = async () => {
    setDl(true);
    try { await download("/bilan/export", params, `bilan_${annee}.xlsx`); } catch (e) { toast.error(errMsg(e)); } finally { setDl(false); }
  };

  return (
    <div data-testid="bilan-page">
      <PageHeader eyebrow="Pour l'expert-comptable" title={`Bilan annuel ${annee}`}>
        <div className="flex flex-wrap gap-2">
          <Select value={annee} onValueChange={setAnnee}>
            <SelectTrigger className="h-9 w-28 bg-white" data-testid="bilan-annee-select"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y} data-testid={`bilan-annee-${y}`}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={exportXlsx} disabled={dl} className="bg-emerald-700 hover:bg-emerald-800" data-testid="bilan-export-button">
            {dl ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Exporter (Excel)
          </Button>
          <Button onClick={exportTableaux} disabled={tous || !data} variant="outline" className="bg-white" title="Un onglet par tableau affiché, dans l'ordre et le tri choisis, avec une page Résumé" data-testid="bilan-export-tableaux-button">
            {tous ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" />}Tous les tableaux (Excel)
          </Button>
          <Button onClick={exportPdf} disabled={pdf} variant="outline" className="bg-white" data-testid="bilan-pdf-button">
            {pdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}Rapport PDF
          </Button>
        </div>
      </PageHeader>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label="Revenus bruts" value={money(data.totaux.revenus_bruts)} sub={`${data.totaux.nb_reservations} réservations · ${data.totaux.nuits} nuits${data.totaux.revenus_directs ? ` · dont ${money(data.totaux.revenus_directs)} loyers et courte durée hors plateformes` : ""}`} testId="bilan-kpi-revenus" />
            <KpiCard label="Commissions" value={money(data.totaux.commission)} testId="bilan-kpi-commissions" />
            <KpiCard label="Bénéfice net" value={money(data.totaux.benefice_net)} sub={`Charges affectées : ${money(data.totaux.charges)}`} tone={data.totaux.benefice_net < 0 ? "danger" : "good"} testId="bilan-kpi-benefice" />
            <KpiCard label="TPS + TVQ attendues" value={money(data.totaux.tps_attendue + data.totaux.tvq_attendue)} sub={`Remises par les plateformes : ${money(data.totaux.taxes_versees)}`} testId="bilan-kpi-taxes" />
            <KpiCard label="Écart à régulariser" value={money(data.totaux.ecart)} tone={data.totaux.ecart > 0 ? "danger" : "good"} sub={`${data.totaux.nb_erreurs} réservation(s) en erreur`} testId="bilan-kpi-ecart" />
          </div>
          <Panel title="Par logement" testId="bilan-logements-panel">
            {data.logements.length ? <LogementsTable rows={data.logements} totaux={data.totaux} onFiche={exportFiche} fiche={fiche} /> : <p className="text-sm text-slate-500">Aucune réservation pour {annee}.</p>}
          </Panel>
          <Panel title="Par trimestre et déclarations" testId="bilan-trimestres-panel">
            <TrimestresTable annee={Number(annee)} rows={data.trimestres} onDeclare={setDeclare} onUndo={undo} />
          </Panel>
          <FraisBookingPanel frais={data.frais_booking} />
          <OccupationPanel occupation={data.occupation} />
          <ThTropPanel groupes={data.th_trop_percue || []} total={data.totaux.th_trop_percu || 0} />
          <PenalitesPanel penalites={data.penalites || []} total={data.totaux.penalites || {}} />
          <ThPercuePanel lignes={data.th_percues || []} total={data.totaux.th_percue || 0} />
          <DepensesPanel depenses={data.depenses} totaux={data.totaux} />
          <HypothequePanel annee={Number(annee)} onChange={load} />
        </div>
      )}
      <DeclareDialog target={declare} onClose={() => setDeclare(null)} onDone={() => { setDeclare(null); load(); }} />
    </div>
  );
}
